"use client";

import React from "react";
import "./ArticleCardSkeleton.css";

export type ArticleVariant =
  | "headline"   // just big title + a couple of lines
  | "card"       // image (optional) + title + excerpt + meta
  | "list"       // compact row for list views
  | "media"      // avatar/media-left layout
  | "full";      // long form: title + multiple paragraphs

export interface ArticleCardSkeletonProps {
  variant?: ArticleVariant;
  /** Show an image block (card/full) or media thumbnail (media) */
  showImage?: boolean;
  /** Show author/avatar chip (media/list/card) */
  showAvatar?: boolean;
  /** Show meta rows (date, read-time) */
  showMeta?: boolean;
  /** Number of excerpt/paragraph lines */
  lines?: number;
  /** Use as independent block (normal flow) */
  standalone?: boolean;
  /** Add your own class */
  className?: string;
  /** For image block: force aspect ratio */
  imageAspect?: "16-9" | "4-3" | "1-1" | "21-9";
}

/** Map aspect to a utility class */
const aspectClass: Record<NonNullable<ArticleCardSkeletonProps["imageAspect"]>, string> = {
  "16-9": "ask-aspect-16-9",
  "4-3": "ask-aspect-4-3",
  "1-1": "ask-aspect-1-1",
  "21-9": "ask-aspect-21-9",
};

/** Minimal shimmering text line */
const Line: React.FC<{ w?: number | string }> = ({ w = "100%" }) => (
  <div className="ask-line" style={{ width: typeof w === "number" ? `${w}%` : w }} />
);

/** Circle (avatar) */
const Avatar: React.FC = () => <div className="ask-avatar" />;

/** Thumbnail / image block */
const Thumb: React.FC<{ aspect: ArticleCardSkeletonProps["imageAspect"] }> = ({ aspect = "16-9" }) => (
  <div className={["ask-thumb", aspectClass[aspect]].join(" ")}>
    <div className="ask-thumb-shimmer" />
  </div>
);

const ArticleCardSkeleton: React.FC<ArticleCardSkeletonProps> = ({
  variant = "card",
  showImage = variant === "card" || variant === "full",
  showAvatar = variant === "media" || variant === "list" || variant === "card",
  showMeta = true,
  lines = variant === "full" ? 6 : variant === "headline" ? 2 : 3,
  standalone = true,
  className = "",
  imageAspect = "16-9",
}) => {
  return (
    <div
      className={[
        "ask-root",
        standalone ? "ask-standalone" : "ask-overlay",
        `ask-${variant}`,
        className || "",
      ].join(" ").trim()}
      aria-hidden="true"
    >
      {/* media-left layout */}
      {variant === "media" && (
        <div className="ask-media-row">
          {showImage ? <Thumb aspect="1-1" /> : showAvatar ? <Avatar /> : <div className="ask-media-gap" />}
          <div className="ask-col">
            <Line w={72} />
            <Line w={55} />
            {showMeta && <div className="ask-meta"><Line w={28} /><Line w={18} /></div>}
          </div>
        </div>
      )}

      {/* list/compact row */}
      {variant === "list" && (
        <div className="ask-list-row">
          {showAvatar && <Avatar />}
          <div className="ask-col">
            <Line w={68} />
            {showMeta && <div className="ask-meta"><Line w={24} /><Line w={18} /></div>}
          </div>
        </div>
      )}

      {/* headline only */}
      {variant === "headline" && (
        <div className="ask-col">
          <Line w={84} />
          <Line w={62} />
          {showMeta && <div className="ask-meta"><Line w={22} /><Line w={16} /></div>}
        </div>
      )}

      {/* card/full (with optional image, excerpt lines) */}
      {(variant === "card" || variant === "full") && (
        <div className="ask-col">
          {showImage && <Thumb aspect={imageAspect} />}

          {/* Title */}
          <div className="ask-title">
            <Line w={86} />
            <Line w={64} />
          </div>

          {/* Excerpt / paragraphs */}
          <div className={`ask-lines ${variant === "full" ? "ask-lines--dense" : ""}`}>
            {Array.from({ length: lines }).map((_, i) => (
              <Line key={i} w={i === lines - 1 ? 52 : 100} />
            ))}
          </div>

          {/* Meta */}
          {showMeta && (
            <div className="ask-meta">
              {showAvatar && <Avatar />}
              <Line w={18} />
              <Line w={14} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ArticleCardSkeleton;